<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import {
  computed, nextTick, ref, watch,
} from 'vue';

// eslint-disable-next-line import/order
import type { Modules } from '~/types/equipment';

import ModalForm from '~/components/shared/ModalForm.vue';
import SearchableSelect from '~/components/shared/SearchableSelect.vue';
import TagInput from '~/components/shared/TagInput.vue';
import {
  EQUIPMENT_LABELS,
  FORM_HELP,
  FORM_PLACEHOLDERS,
  getSkusDuplicateMessage,
  MODAL_TITLES,
} from '~/constants/equipment';
import { useBrandsOptions } from '~/shared/hooks/useBrands';

import { useCreateModule, useModuleById, useUpdateModule } from '../queries/useModulesCrud';
import { moduleSchema } from '../schemas/moduleSchema';

export interface ModuleModalProps {
  mode: 'create' | 'edit' | 'view';
  modelValue: boolean;
  moduleData?: Modules.Module | null;
  moduleId?: null | number;
}

const props = withDefaults(defineProps<ModuleModalProps>(), {
  moduleData: null,
  moduleId: null,
});

const emit = defineEmits<{
  success: [];
  'update:modelValue': [value: boolean];
}>();

const selectedModuleId = ref<null | number>(props.moduleId);
const modalFormRef = ref();

const createMutation = useCreateModule();
const updateMutation = useUpdateModule();

const { isLoading: isLoadingBrands, options: brandsOptions } = useBrandsOptions('modules');

const { data: fetchedModuleData } = useModuleById(selectedModuleId);

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setValues,
} = useForm({
  initialValues: {
    brand: '',
    is_enabled: true,
    model: '',
    power: 0,
    skus: null,
  },
  validateOnMount: false,
  validationSchema: toTypedSchema(moduleSchema),
});

const fieldOptions = {
  validateOnBlur: true,
  validateOnChange: false,
  validateOnInput: true,
  validateOnModelUpdate: false,
};

const [brand] = defineField('brand', fieldOptions);
const [isEnabled, isEnabledProps] = defineField('is_enabled', fieldOptions);
const [model, modelProps] = defineField('model', fieldOptions);
const [power, powerProps] = defineField('power', fieldOptions);
const [skus] = defineField('skus', fieldOptions);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const modalTitle = computed(() => {
  switch (props.mode) {
    case 'create':
      return MODAL_TITLES.MODULE.CREATE;
    case 'edit':
      return MODAL_TITLES.MODULE.EDIT;
    case 'view':
      return MODAL_TITLES.MODULE.VIEW;
    default:
      return 'Módulo';
  }
});

const modalIsLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);

watch(() => props.moduleId, (newId) => {
  selectedModuleId.value = newId;
});

watch(() => props.moduleData, (data) => {
  if (data && (props.mode === 'edit' || props.mode === 'view')) {
    nextTick(() => {
      setValues({
        brand: data.brand || '',
        is_enabled: data.is_enabled,
        model: data.model || '',
        power: data.power,
        skus: data.skus?.length ? data.skus : null,
      });
    });
  }
}, { immediate: true });

watch(() => fetchedModuleData.value, (freshData) => {
  if (freshData && (props.mode === 'edit' || props.mode === 'view') && !props.moduleData) {
    nextTick(() => {
      setValues({
        brand: freshData.brand || '',
        is_enabled: freshData.is_enabled,
        model: freshData.model || '',
        power: freshData.power,
        skus: freshData.skus?.length ? freshData.skus : null,
      });
    });
  }
}, { immediate: true });

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    nextTick(() => {
      resetForm();
      selectedModuleId.value = null;
    });
  } else {
    selectedModuleId.value = props.moduleId;

    if (props.mode === 'create') {
      nextTick(() => {
        resetForm();
        setValues({
          brand: '',
          is_enabled: true,
          model: '',
          power: undefined,
        });
      });
    } else if (props.mode === 'edit' || props.mode === 'view') {
      if (props.moduleData) {
        const data = props.moduleData;
        nextTick(() => {
          setValues({
            brand: data.brand || '',
            is_enabled: data.is_enabled,
            model: data.model || '',
            power: data.power,
            skus: data.skus?.length ? data.skus : null,
          });
        });
      }
    }
  }
}, { immediate: false });

const handleSave = handleSubmit(async (values) => {
  try {
    const apiData = {
      brand: values.brand,
      model: values.model,
      power: values.power,
      skus: values.skus?.length ? values.skus : [],
      ...(props.mode !== 'create' && { is_enabled: values.is_enabled }),
    };

    if (props.mode === 'create') {
      const createData = {
        brand: values.brand,
        is_enabled: true,
        model: values.model,
        power: values.power,
        skus: values.skus?.length ? values.skus : [],
      };
      await createMutation.mutateAsync(createData);
    } else {
      const id = props.moduleData?.id || selectedModuleId.value;
      if (id) {
        await updateMutation.mutateAsync({
          data: apiData,
          id,
        });
      }
    }

    emit('success');
    modalFormRef.value?.resetEditingState();
    isVisible.value = false;
  } catch (error) {
    // Error handled by mutation
  }
});

const handleCancel = () => {
  isVisible.value = false;
};

const handleEdit = () => {
  emit('update:modelValue', false);
  nextTick(() => {
    emit('update:modelValue', true);
  });
};

</script>

<template>
  <ModalForm
    ref="modalFormRef"
    v-model="isVisible"
    :title="modalTitle"
    :mode="mode"
    :loading="modalIsLoading"
    size="lg"
    @save="handleSave"
    @edit="handleEdit"
    @cancel="handleCancel"
  >
    <template
      #default="{
        isViewMode: modalIsViewMode, isLoading: modalIsLoading,
      }">
      <div class="row">
        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="brand">{{ EQUIPMENT_LABELS.BRAND }}</label>
            <SearchableSelect
              id="brand"
              v-model="brand"
              :options="brandsOptions"
              :loading="isLoadingBrands"
              :disabled="modalIsViewMode || modalIsLoading"
              :has-error="!!errors.brand"
              :error-message="errors.brand"
              :placeholder="FORM_PLACEHOLDERS.BRAND_SEARCH"
              loading-text="Carregando marcas..."
              no-options-text="Nenhuma marca encontrada"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="model">{{ EQUIPMENT_LABELS.MODEL }}</label>
            <input
              id="model"
              v-model="model"
              v-bind="modelProps"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.model }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.MODEL"
            />
            <div v-if="errors.model" class="invalid-feedback">
              {{ errors.model }}
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="power">{{ EQUIPMENT_LABELS.POWER }}</label>
            <input
              id="power"
              v-model.number="power"
              v-bind="powerProps"
              type="number"
              min="0.1"
              max="999"
              step="0.1"
              class="form-control"
              :class="{ 'is-invalid': errors.power }"
              :disabled="modalIsViewMode || modalIsLoading"
              :placeholder="FORM_PLACEHOLDERS.POWER"
            />
            <div v-if="errors.power" class="invalid-feedback">
              {{ errors.power }}
            </div>
          </div>
        </div>

        <div v-if="mode !== 'create'" class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="is_enabled">{{ EQUIPMENT_LABELS.STATUS }}</label>
            <select
              id="is_enabled"
              v-model="isEnabled"
              v-bind="isEnabledProps"
              class="form-select"
              :class="{ 'is-invalid': errors.is_enabled }"
              :disabled="modalIsViewMode || modalIsLoading"
            >
              <option :value="true">Ativado</option>
              <option :value="false">Desativado</option>
            </select>
            <div v-if="errors.is_enabled" class="invalid-feedback">
              {{ errors.is_enabled }}
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="mb-3">
            <TagInput
              id="skus"
              v-model="skus"
              :label="EQUIPMENT_LABELS.SKUS"
              :placeholder="FORM_PLACEHOLDERS.SKUS"
              :help-text="FORM_HELP.SKUS"
              item-label="SKU"
              :duplicate-message="getSkusDuplicateMessage"
              :disabled="modalIsViewMode || modalIsLoading"
              :error="errors.skus"
              :invalid="!!errors.skus"
            />
          </div>
        </div>
      </div>
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control:disabled,
.form-select:disabled {
  background-color: #f8f9fa;
  opacity: 1;
}
</style>
